import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MenuAdmin } from '../../../Layout/Sidebar/MenuAdmin';
import { MenuCustom } from '../../../Layout/Sidebar/MenuCustom';
import { MenuSadmin } from '../../../Layout/Sidebar/MenuSadmin';
import { MenuTenant } from '../../../Layout/Sidebar/MenuTenant';
import CustomContext from '../../../_helper/Customizer';
import { resolveIcon, ChevronRight, ChevronLeft, LogOut, Search, Map, LayoutDashboard, ArrowRightLeft, Building } from './iconMap';
import { useAuth, canViewDigitalTwin, canImportFloorPlan } from '../hooks/useAuth';
import { useChatbotAccess, openChatbot } from '../hooks/useChatbotAccess';
import { ROVER_URL, openExternalApp } from '../Shared/externalApps';
import ThemePicker from './ThemePicker';
import LanguagePicker from './LanguagePicker';
import egoalIcon from '../../../assets/images/logo/logo-icon.png';
import './v3-sidebar.css';
import './classic-dark.css';

// External product link. `external` routes it through window.open in MenuItem
// instead of the router — there is no such route in this app.
const ROVER_ITEM = {
    href: ROVER_URL,
    external: true,
    icon: 'external-link',
    title: 'EGoal Rover',
    type: 'link',
};

const COLLAPSE_KEY = 'v3_sidebar_collapsed';
const GROUPS_KEY = 'v3_sidebar_collapsed_groups';
// One-time flag so the "collapse all but V3 Experience" default is applied once,
// after which the user's manual section toggles persist normally.
const GROUPS_DEFAULT_FLAG = 'v3_sidebar_groups_defaulted';

// Display-only rename for the legacy "DashBoard" menu group → "Classic Dashboards".
// Keeps the underlying menutitle (used as a key) untouched.
function groupLabel(menutitle) {
    if (menutitle && menutitle.toLowerCase() === 'dashboard') return 'Classic Dashboards';
    return menutitle;
}

/**
 * V3 Sidebar — glassmorphic, Linear-inspired.
 *
 * Drop-in replacement for Layout/Sidebar/index.jsx.
 * Reads the SAME menu data (MenuAdmin/MenuCustom/MenuSadmin/MenuTenant) so all
 * existing permission filtering and route wiring works unchanged.
 */
export default function V3Sidebar() {
    const { t } = useTranslation();
    const customizer = useContext(CustomContext) || {};
    const { firstName, userRole, userName, concessionaireName, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    // Same gate the chatbot FAB uses (role + route + per-concessionnaire
    // features.ai_chatbot), so the "Ask anything…" bar can never outlive the
    // launcher it drives and become a dead button.
    const chatbotEnabled = useChatbotAccess();

    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
    const [expandedKeys, setExpandedKeys] = useState(new Set());

    // Mobile drawer: the sidebar is translated off-screen at ≤768px (see
    // v3-sidebar.css). This opens/closes it. Closes automatically on navigation
    // so tapping a menu link on a phone dismisses the drawer.
    const [mobileOpen, setMobileOpen] = useState(false);
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    // Portaled tooltip shown when hovering items in the collapsed sidebar (the
    // CSS ::after tooltip is clipped by the sidebar's overflow + backdrop-filter).
    const [tip, setTip] = useState(null); // { label, top } | null
    const showTip = useCallback((label, el) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        setTip({ label, top: r.top + r.height / 2 });
    }, []);
    const hideTip = useCallback(() => setTip(null), []);

    // Auto-collapse the sidebar while the Add-Widget catalog is open; restore the
    // user's previous state when it closes. Driven by WidgetGrid via an event.
    const savedCollapse = useRef(null);
    useEffect(() => {
        const onCatalog = (e) => {
            if (e.detail) {
                if (savedCollapse.current === null) savedCollapse.current = collapsed;
                setCollapsed(true);
            } else if (savedCollapse.current !== null) {
                setCollapsed(savedCollapse.current);
                savedCollapse.current = null;
            }
        };
        window.addEventListener('v3-catalog-open', onCatalog);
        return () => window.removeEventListener('v3-catalog-open', onCatalog);
    }, [collapsed]);
    // Which menu sections (by menutitle) the user has collapsed. Persisted.
    const [collapsedGroups, setCollapsedGroups] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(GROUPS_KEY) || '[]')); }
        catch { return new Set(); }
    });

    // Accordion behaviour: expanding a section collapses every other section,
    // so at most one is open at a time. Collapsing the open one leaves all
    // sections closed. `allTitles` is the full set of section titles, supplied
    // by the caller (menu isn't in scope at this point).
    const toggleGroup = useCallback((title, allTitles = []) => {
        setCollapsedGroups(prev => {
            let next;
            if (prev.has(title)) {
                // Currently collapsed → expand it, collapse all others.
                next = new Set(allTitles.filter(t => t && t !== title));
            } else {
                // Currently expanded → just collapse it.
                next = new Set(prev);
                next.add(title);
            }
            try { localStorage.setItem(GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // Sync collapse state to document body (so page-wrapper can shift)
    useEffect(() => {
        document.body.classList.add('v3-has-sidebar');
        document.body.classList.toggle('v3-sidebar-collapsed', collapsed);
        localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
        return () => {
            document.body.classList.remove('v3-has-sidebar');
            document.body.classList.remove('v3-sidebar-collapsed');
        };
    }, [collapsed]);

    // Pick menu based on user type (mirrors old Sidebar/index.jsx logic).
    // MenuAdmin/MenuCustom use localStorage spreads that freeze at import time,
    // so we deep-clone and patch license items at render time when the flags are set.
    const rawMenu = useMemo(() => {
        const userType = localStorage.getItem('usertype');
        let base;
        switch (userType) {
            case 'Super Admin':
            case 'sadmin':      base = MenuSadmin; break;
            case 'admin':
            case 'Mall Admin':  base = MenuAdmin; break;
            case 'Backend User': base = MenuAdmin; break;
            case 'Tenant':
            case 'tenant':
                // Work Permit / NOC is roadmap on the tenant portal. MenuTenant
                // declares it unconditionally and the tenant menu is otherwise
                // handed through unfiltered, so every tenant saw it — leading to
                // a form with no templates, on an endpoint that 401s because the
                // tenant JWT carries no concessionaire_id.
                //
                // Gated on the per-client features.work_permit_tenant flag,
                // resolved at login. Absent flag → hidden.
                base = localStorage.getItem('featureWorkPermitTenant') === '1'
                    ? MenuTenant
                    : MenuTenant.filter(g => g.menutitle !== 'Operations');
                break;
            default:            base = MenuCustom; break;
        }

        // ── Invoicing gate ────────────────────────────────────────────────
        // Applied HERE rather than in SidebarMenuItems.filterMenuItems: this
        // component reads MenuAdmin/MenuCustom directly and never calls that
        // helper, so a gate there has no effect on the V3 shell.
        //
        // Evaluated at render time for the same reason the license patch below
        // is — the localStorage spreads inside the menu modules freeze at import,
        // which on the login page is before any permission flag exists.
        const isAdminRole = userType === 'admin' || userType === 'Mall Admin';
        const canViewInvoicing = isAdminRole
            || localStorage.getItem('accessInvoicing') === '1'
            || localStorage.getItem('manageInvoicing') === '1';
        const canManageInvoicing = isAdminRole
            || localStorage.getItem('manageInvoicing') === '1';

        base = base
            .filter(g => g.menutitle !== 'Invoicing' || canViewInvoicing)
            .map(g => (g.menutitle === 'Invoicing' && !canManageInvoicing
                // View-only: drop Generate / Settings, keep the register.
                ? { ...g, Items: (g.Items || []).filter(i => !i.requiresManage) }
                : g));

        const hasLicense = localStorage.getItem('accessLicenses') === '1'
            || localStorage.getItem('manageLicenses') === '1';
        if (!hasLicense) return base;
        const licenseItem = {
            path: `${process.env.PUBLIC_URL}/admin/licenses`,
            icon: 'to-do',
            title: 'License Inventory',
            type: 'link',
        };
        const alreadyHas = base.some(g =>
            (g.Items || []).some(i => i.title === 'License Inventory'));
        if (alreadyHas) return base;
        return base.map(g => {
            if (g.menutitle === 'General Settings') {
                return { ...g, Items: [...(g.Items || []), licenseItem] };
            }
            return g;
        });
    }, [userRole]);

    // Prepend V3 group at the top (Spatial, Dashboard, Compare).
    // Sadmin runs in the legacy /controlpanel surface — none of the /v3/* routes
    // apply to them, so we skip the v3 group for that role.
    const menu = useMemo(() => {
        const userType = localStorage.getItem('usertype');
        // sadmin runs the legacy control panel; tenants get their own scoped
        // portal menu — neither should see the V3 Experience analytics group.
        if (userType === 'sadmin' || userType === 'Super Admin'
            || (userType || '').toLowerCase() === 'tenant') {
            return Array.isArray(rawMenu) ? rawMenu : [];
        }
        const v3Group = {
            menutitle: 'V3 Experience',
            Items: [
                { path: `${process.env.PUBLIC_URL}/v3/spatial`, icon: 'spatial', title: 'Spatial Brief', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/dashboard`, icon: 'dashboard', title: 'Smart Dashboard', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/tenant`, icon: 'tenant', title: 'Tenant View', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/command`, icon: 'brain', title: 'Command Center', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/anomalies`, icon: 'alert-triangle', title: 'Anomalies', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/seasonal`, icon: 'calendar', title: 'Seasonal Events', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/analytics`, icon: 'analytics', title: 'Analytics', type: 'link', badge: 'NEW' },
                { path: `${process.env.PUBLIC_URL}/v3/compare`, icon: 'compare', title: 'Compare Anything', type: 'link', badge: 'NEW' },
            ],
        };

        /*
         * Digital Twin, and separately the importer that authors the model it
         * draws. Two entitlements, because they are two rights — see
         * hooks/useAuth. Both are org-level: whether the CLIENT has the module,
         * with no per-seat role test on top.
         *
         * Hidden here AND refused by the router (see Route/RequireAccess) — a
         * menu that omits a link still leaves the URL typeable.
         */
        if (canViewDigitalTwin()) {
            v3Group.Items.push(
                { path: `${process.env.PUBLIC_URL}/v3/mallmap`, icon: 'floor', title: 'Digital Twin', type: 'link', badge: 'NEW' },
            );
        }
        if (canImportFloorPlan()) {
            v3Group.Items.push(
                { path: `${process.env.PUBLIC_URL}/v3/mallmap/import`, icon: 'file', title: 'Import Floor Plan', type: 'link', badge: 'NEW' },
            );
        }
        // EGoal Rover — a separate product, not a route in this app. Sits in its
        // own unlabelled group so it reads as a sibling of the V3 Experience
        // section rather than another page inside it.
        const roverGroup = {
            menutitle: null,
            Items: [ROVER_ITEM],
        };

        return [v3Group, roverGroup, ...(Array.isArray(rawMenu) ? rawMenu : [])];
    }, [rawMenu]);

    // All section titles — fed to toggleGroup so it can run the accordion.
    const groupTitles = useMemo(() => menu.map(g => g.menutitle).filter(Boolean), [menu]);

    // First-run default: collapse every section EXCEPT "V3 Experience" so the V3
    // nav leads and the legacy groups (Classic Dashboards / Reports / Settings…)
    // stay tucked away. Applied once (flagged), then the user's own toggles win.
    useEffect(() => {
        if (localStorage.getItem(GROUPS_DEFAULT_FLAG)) return;
        if (!Array.isArray(menu) || menu.length === 0) return;
        const hasV3 = menu.some(g => (g.menutitle || '').toLowerCase() === 'v3 experience');
        // Skip for surfaces without the V3 group (e.g. sadmin control panel) so we
        // don't collapse their entire nav — just mark the default as applied.
        if (!hasV3) { try { localStorage.setItem(GROUPS_DEFAULT_FLAG, '1'); } catch { /* ignore */ } return; }
        const titles = menu
            .map(g => g.menutitle)
            .filter(t => t && t.toLowerCase() !== 'v3 experience');
        setCollapsedGroups(new Set(titles));
        try {
            localStorage.setItem(GROUPS_KEY, JSON.stringify(titles));
            localStorage.setItem(GROUPS_DEFAULT_FLAG, '1');
        } catch { /* ignore */ }
    }, [menu]);

    const isActive = useCallback((path) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    }, [location.pathname]);

    const toggleExpand = useCallback((key) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);

    // Auto-expand any submenu whose child is the current route
    useEffect(() => {
        const nextExpanded = new Set();
        for (const group of menu) {
            for (const item of group.Items || []) {
                if (item.children?.some(c => isActive(c.path) || c.children?.some(cc => isActive(cc.path)))) {
                    nextExpanded.add(`${group.menutitle}:${item.title}`);
                }
            }
        }
        setExpandedKeys(nextExpanded);
    }, [menu, location.pathname, isActive]);

    return (
        <>
            {/* Mobile-only menu FAB (bottom-left, mirrors the chat FAB). Hidden on
                desktop and while the drawer is open. */}
            <button
                type="button"
                className={`v3-mobile-menu-btn${mobileOpen ? ' is-hidden' : ''}`}
                onClick={() => setMobileOpen(true)}
                aria-label={t('Open menu')}
                aria-expanded={mobileOpen}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
            </button>

            {/* Tap-to-close scrim (mobile, only while open). */}
            <div
                className={`v3-mobile-scrim${mobileOpen ? ' is-open' : ''}`}
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
            />

            <aside className={`v3-sidebar v3 ${collapsed ? 'collapsed' : ''}${mobileOpen ? ' v3-mobile-open' : ''}`}>
            {/* Header */}
            <div className="v3-sidebar-header">
                <div className="v3-sidebar-logo"><img src={egoalIcon} alt="EGoal" /></div>
                <div className="v3-sidebar-brand-block">
                    <span className="v3-sidebar-brand">EGoal V3</span>
                    <span className="v3-sidebar-brand-sub">{t('Retail Intelligence')}</span>
                </div>
                <button
                    className="v3-sidebar-collapse-btn"
                    onClick={() => setCollapsed(c => !c)}
                    aria-label={collapsed ? t('Expand sidebar') : t('Collapse sidebar')}
                    title={collapsed ? t('Expand') : t('Collapse')}
                >
                    {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
            </div>

            {/* Switch-mall shortcut — only for Backend Users, who own multiple
                malls and need to bounce back to the mall picker from any page. */}
            {localStorage.getItem('usertype') === 'Backend User' && (
                <div
                    className="v3-menu-item v3-sidebar-switch-mall"
                    onClick={() => {
                        const layout = localStorage.getItem('sidebar_layout') || 'compact-wrapper';
                        navigate(`${process.env.PUBLIC_URL}/admin/home/${layout}`);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const layout = localStorage.getItem('sidebar_layout') || 'compact-wrapper';
                            navigate(`${process.env.PUBLIC_URL}/admin/home/${layout}`);
                        }
                    }}
                    title={t('Switch mall')}
                    data-tooltip={collapsed ? t('Switch mall') : undefined}
                >
                    <div className="v3-menu-item-icon"><ArrowRightLeft size={16} /></div>
                    {!collapsed && <span className="v3-menu-item-label">{t('Switch mall')}</span>}
                </div>
            )}

            {/* Property selector */}
            {!collapsed && concessionaireName && (
                <div className="v3-sidebar-property">
                    <div className="v3-property-chip">
                        <Building size={13} className="v3-property-chip-icon" />
                        <span className="v3-property-chip-name">{concessionaireName}</span>
                        <span className="v3-property-chip-dot" />
                    </div>
                </div>
            )}

            {/* Search / NL query bar — only when the chatbot it opens exists */}
            {chatbotEnabled && (
                <div className="v3-sidebar-search">
                    <button className="v3-search-trigger" onClick={openChatbot} title={t('Ask AI (Ctrl+K)')}>
                        <Search size={14} className="v3-search-icon" />
                        <span>{t('Ask anything…')}</span>
                        <span className="v3-kbd">Ctrl+K</span>
                    </button>
                </div>
            )}

            {/* Scrollable menu */}
            <div className="v3-sidebar-scroll">
                {menu.filter(group => (group.Items || []).length > 0).map((group, gi) => {
                    const title = group.menutitle;
                    // Section-collapse only applies in the expanded sidebar; in icon-only
                    // mode the labels are hidden, so items stay visible regardless.
                    const groupCollapsed = !collapsed && !!title && collapsedGroups.has(title);
                    return (
                        <div key={(title || '') + gi} className="v3-menu-group">
                            {title && (
                                <button
                                    type="button"
                                    className={`v3-menu-group-label${collapsed ? '' : ' clickable'}${groupCollapsed ? ' is-collapsed' : ''}`}
                                    onClick={() => { if (!collapsed) toggleGroup(title, groupTitles); }}
                                    aria-expanded={!groupCollapsed}
                                    title={groupCollapsed ? t('Expand section') : t('Collapse section')}
                                >
                                    <span className="v3-menu-group-label-text">{t(groupLabel(title))}</span>
                                    {!collapsed && <ChevronRight size={12} className="v3-menu-group-chevron" />}
                                </button>
                            )}
                            {!groupCollapsed && (group.Items || []).map((item, ii) => (
                                <MenuItem
                                    key={`${title}:${item.title || ii}`}
                                    item={item}
                                    groupKey={title}
                                    isActive={isActive}
                                    expanded={expandedKeys}
                                    onToggleExpand={toggleExpand}
                                    collapsed={collapsed}
                                    navigate={navigate}
                                    showTip={showTip}
                                    hideTip={hideTip}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* Footer — compact account bar */}
            <div className="v3-account-bar">
                <div
                    className="v3-avatar"
                    onMouseEnter={(e) => { if (collapsed) showTip(userName || firstName || t('Profile'), e.currentTarget); }}
                    onMouseLeave={hideTip}
                >
                    {(userName || firstName || 'U').slice(0, 1).toUpperCase()}
                </div>
                {!collapsed && (
                    <div className="v3-footer-meta">
                        <span className="name">{firstName || userName || t('User')}</span>
                        <span className="role">{userRole || '—'}</span>
                    </div>
                )}
                <div className="v3-account-actions">
                    <LanguagePicker />
                    <ThemePicker />
                    <button
                        className="v3-account-btn"
                        onClick={logout}
                        aria-label={t('Logout')}
                        data-tooltip={t('Logout')}
                    >
                        <LogOut size={16} />
                    </button>
                </div>
            </div>

            {/* Collapsed-sidebar tooltip — portaled to <body> so it isn't clipped
                by the sidebar's overflow + backdrop-filter. */}
            {collapsed && tip && createPortal(
                <div className="v3-collapsed-tip" style={{ top: tip.top }}>{tip.label}</div>,
                document.body,
            )}
            </aside>
        </>
    );
}

function MenuItem({ item, groupKey, isActive, expanded, onToggleExpand, collapsed, navigate, showTip, hideTip }) {
    const { t } = useTranslation();
    const hasChildren = item.type === 'sub' && Array.isArray(item.children);
    const key = `${groupKey}:${item.title}`;
    const isOpen = expanded.has(key);

    // For link items, active if path matches
    const active = !hasChildren && isActive(item.path);

    // For submenu items, active if any child matches
    const subActive = hasChildren && item.children.some(c => isActive(c.path));

    const onClick = () => {
        if (hasChildren) {
            onToggleExpand(key);
            return;
        }
        // Links out of the app: open in a new tab and hand over no reference to
        // this window. Without noopener the target page can reach back through
        // window.opener and redirect the app behind the user's back.
        if (item.external && item.href) {
            openExternalApp(item.href);
            return;
        }

        if (!item.path) return;

        // V3 routes (under /v3/*) don't take a :layout suffix — navigate as-is.
        // V2 routes (admin/sadmin/tenant) all end in /:layout — match V2 sidebar
        // behavior by appending the current layout id from the URL or localStorage.
        // Strip the PUBLIC_URL (/v3.0) prefix only to CLASSIFY the route; always
        // navigate with the full item.path so the /v3.0 sub-path prefix is kept.
        const raw = item.path.replace(process.env.PUBLIC_URL, '');
        if (raw.startsWith('/v3/')) {
            navigate(item.path);
            return;
        }
        // Layout id is the trailing path segment of any v2 route. If we're currently
        // on a v3 page (or root), fall back to the persisted layout, then a default.
        // Strip PUBLIC_URL from the pathname first — under /v3.0 every path starts
        // with "/v3", which would otherwise misclassify v2 pages as v3.
        const PUBLIC_URL = process.env.PUBLIC_URL || '';
        const currentPath = PUBLIC_URL && window.location.pathname.startsWith(PUBLIC_URL)
            ? window.location.pathname.slice(PUBLIC_URL.length)
            : window.location.pathname;
        const lastSeg = currentPath.split('/').filter(Boolean).pop();
        const onV2Route = lastSeg && !currentPath.startsWith('/v3');
        const currentLayout = (onV2Route && lastSeg)
            || localStorage.getItem('layout')
            || localStorage.getItem('sidebar_layout')
            || 'Dubai';
        navigate(`${item.path}/${currentLayout}`);
    };

    return (
        <>
            <div
                className={`v3-menu-item ${active || subActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                onClick={onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
                onMouseEnter={(e) => { if (collapsed && showTip) showTip(t(item.title), e.currentTarget); }}
                onMouseLeave={() => { if (hideTip) hideTip(); }}
            >
                <div className="v3-menu-item-icon">{resolveIcon(item.icon)}</div>
                <span className="v3-menu-item-label">{t(item.title)}</span>
                {item.badge && !collapsed && typeof item.badge === 'string' && item.badge === 'NEW' && (
                    <span className="v3-menu-item-badge">{t('NEW')}</span>
                )}
                {hasChildren && <ChevronRight size={13} className="v3-menu-item-chevron" />}
            </div>

            {hasChildren && (
                <div className={`v3-submenu ${isOpen ? 'open' : ''}`}>
                    {item.children.map((child, ci) => (
                        <MenuItem
                            key={key + ':' + (child.title || ci)}
                            item={child}
                            groupKey={key}
                            isActive={isActive}
                            expanded={expanded}
                            onToggleExpand={onToggleExpand}
                            collapsed={collapsed}
                            navigate={navigate}
                            showTip={showTip}
                            hideTip={hideTip}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
